import useTravellersTrackingActions from '@pages/Travellers/hooks/useTravellersTrackingActions';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import TravellerInfoForm from '@components/TravellerInfoForm/TravellerInfoForm';
import { TravellerInfoFormSubmitData } from '@components/TravellerInfoForm/types';

import { datadogLogger } from '@helpers/datadogLogger';
import { getNationalIdDoc, getPassportDoc, getTravellerType } from '@helpers/traveller';

import { useCreateTraveller, useUpdateTraveller } from '@hooks/useTravellersApi';

import { getTranslationsState } from '@redux/selectors';

import { Traveller } from '@wegoTypes/traveller';

import styles from './TravellerFormView.module.scss';

interface TravellerFormViewProps {
  traveller?: Traveller;
  onClose: (refreshUser?: boolean) => void;
  displayMessage: (message: string) => void;
  pageViewId: string;
}

const TravellerFormView = ({
  traveller,
  onClose,
  displayMessage,
  pageViewId,
}: TravellerFormViewProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutateAsync: createTraveller } = useCreateTraveller();
  const { mutateAsync: updateTraveller } = useUpdateTraveller();

  const translations = useSelector(getTranslationsState);
  const { trackClickSaveTraveller } = useTravellersTrackingActions(pageViewId);

  const handleFormSubmission = async (data: TravellerInfoFormSubmitData) => {
    trackClickSaveTraveller();
    setIsSubmitting(true);
    try {
      const travellerData = {
        ...data,
        travellerType: getTravellerType(data.dateOfBirth),
        travellerDocuments: [
          ...(data.passportNumber
            ? [
                {
                  documentNumber: data.passportNumber,
                  documentType: 'PASSPORT' as const,
                  expiryDate: data.passportExpiryDate,
                  issuingCountry: data.nationality,
                },
              ]
            : []),
          ...(data.nationalIdNumber
            ? [
                {
                  documentNumber: data.nationalIdNumber,
                  documentType: 'NATIONAL_ID' as const,
                  expiryDate: data.nationalIdExpiryDate,
                  issuingCountry: data.nationality,
                },
              ]
            : []),
        ],
      };
      let responseTraveller: Traveller | undefined;
      if (traveller) {
        responseTraveller = await updateTraveller({
          id: traveller.id,
          ...travellerData,
        });
      } else {
        responseTraveller = await createTraveller(travellerData);
      }
      onClose(responseTraveller?.primary);
      displayMessage(traveller ? translations.traveller_updated : translations.traveller_added);
    } catch (error) {
      displayMessage(translations.something_wrong_try_again);
      datadogLogger(error as Error, {
        description: 'Add/Edit Traveller Form Submission Failed',
        errorSource: 'TravellerFormView',
        states: {
          data,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        {!!traveller ? translations.edit_traveller : translations.add_new_traveller}
      </div>
      <TravellerInfoForm
        onSubmit={handleFormSubmission}
        traveller={
          traveller
            ? {
                firstName: traveller.firstName,
                lastName: traveller.lastName,
                nationality: traveller.nationality,
                gender: traveller.gender,
                dateOfBirth: traveller.dateOfBirth,
                passportNumber: getPassportDoc(traveller.travellerDocuments)?.documentNumber,
                passportExpiryDate:
                  getPassportDoc(traveller.travellerDocuments)?.expiryDate || undefined,
                nationalIdNumber: getNationalIdDoc(traveller.travellerDocuments)?.documentNumber,
                nationalIdExpiryDate:
                  getNationalIdDoc(traveller.travellerDocuments)?.expiryDate || undefined,
              }
            : undefined
        }
        isSubmitting={isSubmitting}
        onCancelBtn={onClose}
        saveBtnText={traveller ? translations.save : translations.add_new_traveller}
      />
    </div>
  );
};

export default TravellerFormView;
